import React, {
  ChangeEvent,
  FocusEvent,
  useContext,
  useEffect,
  useState,
} from "react";
import classNames from "classnames";
import {
  Alert,
  Button,
  Choice,
  InlineError,
  FormLabel,
  TextField,
} from "@cmsgov/ds-medicare-gov";
import { useFlags } from "launchdarkly-react-client-sdk";
import { AppContext } from "../../app/store";
import routes from "../../app/routes";
import {
  County,
  CoverageSelectorFormValues,
  CoverageSelectorInitialValues,
  CoverageSelectorSelectedValues,
  CoverageSelectorCoverageType,
  CoverageSelectorErrorType,
} from "../../@types";
import { getCounties } from "../../api";
import CCXPBaseDrop from "../CCXPBaseDrop";
import { getCoverageTypeLabel, PlanQuestions } from "./PlanQuestions";
import { useTranslate } from "../../helpers/intlHooks";
import { hasMailOrderPharmacy } from "../../app/store/AppStore";
import { isValidZipcode } from "../../helpers/zipcodeHelpers";
import { getYearPartInfo } from "../../helpers/yearFlagHelpers";
import { Link } from "react-router-dom";
import SmoothScroll from "smooth-scroll";
import {
  AnalyticsActionType,
  AnalyticsButton,
  AnalyticsButtonStyle,
  AnalyticsButtonType,
} from "../../app/contexts/Analytics/types";

interface CoverageSelectorProps {
  initialFormValues?: CoverageSelectorInitialValues;
  onApply: (
    values: CoverageSelectorSelectedValues,
    analyticsButton?: AnalyticsButton
  ) => void;
  autoscrollWhenMenuOpens?: boolean;
}

export const CoverageSelector: React.FunctionComponent<
  CoverageSelectorProps
> = props => {
  const t = useTranslate();
  const [fetching, setFetching] = useState(false);
  const { state, dispatch } = useContext(AppContext);
  const flags = useFlags();
  const { language } = state;
  const { mctCurrentPlanYear } = flags;

  const { isPublicPreview, isOpenEnrollment, isOpenEnrollmentNextYearOnly } =
    getYearPartInfo(flags);

  const showCoverageYearSelector =
    isPublicPreview || isOpenEnrollment || isOpenEnrollmentNextYearOnly;

  const needHelpText = t("coverage_selector.need_help_text");
  const needHelpLinkUrl = routes.coverageWizard.options;
  const startButtonText = t("coverage_selector.start");
  const analyticsButton = {
    text: startButtonText,
    buttonStyle: AnalyticsButtonStyle.PRIMARY,
    buttonType: AnalyticsButtonType.SUBMIT,
  };

  const hasSavedPharmaciesAndDrugs =
    (hasMailOrderPharmacy(state) || state.pharmacies.length > 0) &&
    state.prescriptions.length > 0;

  const [currentFormValues, setCurrentFormValues] =
    useState<CoverageSelectorFormValues>({
      coverageYear: showCoverageYearSelector
        ? mctCurrentPlanYear + 1
        : mctCurrentPlanYear,
      zipcode: props.initialFormValues?.zipcode,
      coverageType: props.initialFormValues?.coverageType,
    });

  const [tmpCoverageType, setTmpCoverageType] = useState<
    CoverageSelectorCoverageType | undefined
  >(currentFormValues.coverageType);

  const [hasError, setHasError] = useState<boolean>(false);

  const [zipcodeError, setZipcodeError] = useState<CoverageSelectorErrorType>(
    CoverageSelectorErrorType.NONE
  );
  const [planTypeError, setPlanTypeError] = useState<CoverageSelectorErrorType>(
    CoverageSelectorErrorType.NONE
  );

  const [zipcodePlanTypeErrorMessage, setZipcodePlanTypeErrorMessage] =
    useState<string | undefined>(undefined);

  const [countyErrorMessage, setCountyErrorMessage] = useState<
    string | undefined
  >(undefined);

  const [countyResults, setCountyResults] = useState<County[]>([]);

  const [selectedCoverageTypeLabel, setSelectedCoverageTypeLabel] =
    useState("");

  const [selectedCountyInput, setSelectedCountyInput] =
    useState<HTMLInputElement | null>(null);

  const getSelectedCoverageTypeLabel = (
    coverageType?: CoverageSelectorCoverageType
  ): string => {
    let coverageTypeLabel = t("coverage_selector.select_a_plan_type");
    if (coverageType) {
      const label = getCoverageTypeLabel(coverageType);
      if (label) {
        coverageTypeLabel = label;
      }
    }
    return coverageTypeLabel;
  };

  useEffect(() => {
    if (props.initialFormValues?.zipcode) {
      fetchCounties(
        props.initialFormValues.zipcode,
        props.initialFormValues.fips
      );
    }
  }, []);

  useEffect(() => {
    const coverageTypeLabel = getSelectedCoverageTypeLabel(
      currentFormValues.coverageType
    );

    setSelectedCoverageTypeLabel(coverageTypeLabel);
  }, [currentFormValues.coverageType, language]);

  const createCoverageYearChoiceFor = (year: number, id: string) => (
    <Choice
      key={year}
      type="radio"
      name="coverage-year"
      id={id}
      value={year}
      data-testid={`coverage-selector-coverage-year-${year}`}
      data-cy={id}
      checked={currentFormValues.coverageYear === year}
      onChange={(e: ChangeEvent<HTMLInputElement>) => {
        setCurrentFormValues({
          ...currentFormValues,
          coverageYear: +e.target.value,
        });
      }}
      label={year}
    />
  );

  const validateForm = (): boolean => {
    let tmpHasError = false;
    let tmpZipcodeError = zipcodeError;
    let tmpPlanTypeError = CoverageSelectorErrorType.NONE;

    if (zipcodeError === CoverageSelectorErrorType.API) {
      // want to keep the api error
      tmpZipcodeError = zipcodeError;
      tmpHasError = true;
    } else {
      if (!isValidZipcode(currentFormValues.zipcode)) {
        tmpZipcodeError = CoverageSelectorErrorType.INVALID;
        tmpHasError = true;
      } else {
        // valid zipcode
        if (!currentFormValues.county) {
          setCountyErrorMessage(t("coverage_selector.error.missing_county"));
          tmpHasError = true;
        } else {
          setCountyErrorMessage(undefined);
        }
      }
    }

    if (!currentFormValues.coverageType) {
      tmpPlanTypeError = CoverageSelectorErrorType.MISSING;
      tmpHasError = true;
    }

    setZipcodePlanTypeErrorMessage(
      constructZipcodePlanTypeError(tmpZipcodeError, tmpPlanTypeError)
    );

    setZipcodeError(tmpZipcodeError);
    setPlanTypeError(tmpPlanTypeError);
    setHasError(tmpHasError);
    return tmpHasError;
  };

  const constructZipcodePlanTypeError = (
    zipcodeError: CoverageSelectorErrorType,
    planTypeError: CoverageSelectorErrorType
  ): string | undefined => {
    if (zipcodeError === CoverageSelectorErrorType.API) {
      if (planTypeError === CoverageSelectorErrorType.MISSING) {
        return t(
          "coverage_selector.error.zip_code_not_found_missing_plan_type"
        );
      }
      return t("coverage_selector.error.zip_code_not_found");
    }
    if (
      zipcodeError === CoverageSelectorErrorType.INVALID ||
      zipcodeError === CoverageSelectorErrorType.MISSING
    ) {
      if (planTypeError === CoverageSelectorErrorType.MISSING) {
        return t("coverage_selector.error.missing_zip_code_plan_type");
      }
      return t("coverage_selector.error.zip_code");
    }

    if (planTypeError === CoverageSelectorErrorType.MISSING) {
      return t("coverage_selector.error.plan_type");
    }
    return undefined;
  };

  const fetchCounties = async (
    zipcode: string,
    selectFips?: string
  ): Promise<void> => {
    setFetching(true);
    try {
      const fetchedCounties = await getCounties(zipcode);
      setCountyResults(fetchedCounties);
      let fipsToSelect = selectFips;
      // If a county input is already selected when `fetchCounties` is called, and if
      // another county fips isn't passed in, the selected county should be used
      // to update form values. Otherwise, there are edge-case scenarios where
      // the form will throw an error even though a county is selected.
      if (selectedCountyInput) {
        const county = JSON.parse(selectedCountyInput.value) as County;
        fipsToSelect = fipsToSelect || county?.fips;
      }
      if (fipsToSelect) {
        const foundCounty = fetchedCounties.find(
          county => county.fips === fipsToSelect
        );
        if (foundCounty) {
          setCurrentFormValues({
            ...currentFormValues,
            county: foundCounty,
          });
        } else {
          // initial fips not found
          if (fetchedCounties.length === 1) {
            setCurrentFormValues({
              ...currentFormValues,
              county: fetchedCounties[0],
            });
          }
        }
      } else {
        if (fetchedCounties.length === 1) {
          setCurrentFormValues({
            ...currentFormValues,
            county: fetchedCounties[0],
          });
        }
      }
    } catch (err) {
      setZipcodePlanTypeErrorMessage(
        t("coverage_selector.error.zip_code_not_found")
      );
      setZipcodeError(CoverageSelectorErrorType.API);
    }
    setFetching(false);
  };

  const onZipcodeOut = () => {
    if (currentFormValues.zipcode) {
      if (!isValidZipcode(currentFormValues.zipcode)) {
        setZipcodePlanTypeErrorMessage(t("coverage_selector.error.zip_code"));
        setZipcodeError(CoverageSelectorErrorType.INVALID);
      } else {
        fetchCounties(currentFormValues.zipcode);
      }
    } else {
      if (zipcodeError === CoverageSelectorErrorType.NONE) {
        setZipcodePlanTypeErrorMessage(t("coverage_selector.error.zip_code"));
        setZipcodeError(CoverageSelectorErrorType.MISSING);
      }
    }
  };

  const onZipcodeChange = (e: FocusEvent<HTMLInputElement>) => {
    const zipcodeInput = e.target.value;

    setZipcodePlanTypeErrorMessage(undefined);
    setCountyErrorMessage(undefined);
    setCountyResults([]);
    if (zipcodeInput.length == 0) {
      setZipcodePlanTypeErrorMessage(t("coverage_selector.error.zip_code"));
      setZipcodeError(CoverageSelectorErrorType.MISSING);
    } else {
      setZipcodeError(CoverageSelectorErrorType.NONE);
    }

    setCurrentFormValues({
      ...currentFormValues,
      zipcode: zipcodeInput,
      county: undefined,
    });
  };

  const onClickStart = (e: ChangeEvent<HTMLFormElement>): void => {
    e.preventDefault();
    if (!validateForm()) {
      props.onApply(
        currentFormValues as CoverageSelectorSelectedValues,
        analyticsButton
      );
    }
  };

  useEffect(() => {
    if (hasError) {
      validateForm();
    }
  }, [language]);

  return (
    <div
      className="mct-c-coverage-selector"
      data-testid="mct-c-coverage-selector"
    >
      <form onSubmit={onClickStart}>
        {hasError && (
          <div data-testid="alert" className="ds-u-margin-bottom--2">
            <Alert
              heading={t("coverage_selector.alert.heading")}
              variation="error"
              className="mct-c-coverage-selector__alert"
            >
              <span>{t("coverage_selector.alert.message")}</span>
            </Alert>
          </div>
        )}
        {showCoverageYearSelector && (
          <div data-testid="coverage-year-section">
            <fieldset className="ds-c-fieldset ds-u-margin-top--0">
              <legend className="ds-c-label">
                <span className="ds-u-text-transform--uppercase">
                  {t("coverage_selector.coverage_for")}
                </span>
              </legend>
              <div className="mct-c-coverage-selector__coverage-years">
                {createCoverageYearChoiceFor(
                  mctCurrentPlanYear + 1,
                  "coverage-year-next-year"
                )}
                {!isOpenEnrollmentNextYearOnly &&
                  createCoverageYearChoiceFor(
                    mctCurrentPlanYear,
                    "coverage-year-current-year"
                  )}
              </div>
            </fieldset>
          </div>
        )}
        <div className="ds-u-padding-top--2">
          <div>
            <div className="mct-c-coverage-selector__zipcode-plan-type">
              <TextField
                label={t("coverage_selector.zip_code")}
                labelClassName="ds-h6"
                name="zipcode"
                data-testid="coverage-selector-zipcode"
                className={classNames("mct-c-coverage-selector__zipcode", {
                  "mct-c-coverage-selector__text-field--error":
                    zipcodeError !== CoverageSelectorErrorType.NONE,
                })}
                onBlur={onZipcodeOut}
                onChange={onZipcodeChange}
                defaultValue={currentFormValues.zipcode}
                aria-required="true"
                aria-invalid={zipcodeError !== CoverageSelectorErrorType.NONE}
                {...(zipcodeError !== CoverageSelectorErrorType.NONE
                  ? { "aria-describedby": "zipcode-plantype-error" }
                  : {})}
              />
              <div className="mct-c-coverage-selector__plantype">
                <FormLabel className="ds-text-heading--sm ds-u-margin-top--0">
                  {t("search_results_page.filters.types_of_plans")}
                </FormLabel>
                <CCXPBaseDrop
                  name="coverage-selector-select-plan-type"
                  label={selectedCoverageTypeLabel}
                  onApply={() => {
                    setCurrentFormValues({
                      ...currentFormValues,
                      coverageType: tmpCoverageType,
                    });
                    setPlanTypeError(CoverageSelectorErrorType.NONE);
                  }}
                  onMenuOpen={() => {
                    setTmpCoverageType(currentFormValues.coverageType);

                    if (props.autoscrollWhenMenuOpens) {
                      const scroll = new SmoothScroll();

                      const openedMenuContainer = document.querySelector(
                        ".CCXPBase__menu--open"
                      );

                      scroll.animateScroll(openedMenuContainer, null, {
                        speed: 2000,
                        updateURL: false,
                      });
                    }
                  }}
                  size="wide"
                  hideClear
                  hideCloseButton
                  hasError={planTypeError !== CoverageSelectorErrorType.NONE}
                  buttonType="button"
                  buttonLabelClassName="CCXPBase__button--truncate"
                  applyButtonClassName="e2e-coverage-selector-apply-button"
                >
                  <fieldset className="ds-c-fieldset ds-u-margin-top--0">
                    <legend className="ds-u-visibility--screen-reader">
                      {t("coverage_selector.plan_type")}
                    </legend>
                    <PlanQuestions
                      handleChoice={(e: ChangeEvent<HTMLInputElement>) => {
                        setTmpCoverageType(
                          e.target.value as CoverageSelectorCoverageType
                        );
                      }}
                      coverageSelection={tmpCoverageType}
                    />
                  </fieldset>
                </CCXPBaseDrop>
              </div>
            </div>
            {zipcodePlanTypeErrorMessage && (
              <div
                className=" ds-u-padding-top--1"
                data-testid="zipcodePlanTypeError"
              >
                <InlineError id="zipcode-plantype-error">
                  {zipcodePlanTypeErrorMessage}
                </InlineError>
              </div>
            )}
            {fetching && <p>{t("app.loading")}...</p>}
            {countyResults.length > 1 && (
              <>
                <fieldset className="ds-c-fieldset ds-u-padding-top--1">
                  <legend className="ds-c-label">
                    <span data-testid="select-your-county">
                      {t("coverage_selector.select_your_county")}
                    </span>
                  </legend>
                  <div className="mct-c-coverage-selector__zip-counties">
                    {countyResults.map((county, i) => (
                      <Choice
                        key={i}
                        type="radio"
                        name="county"
                        data-testid={`coverage-selector-fips-${county.fips}`}
                        id={county.fips}
                        inputRef={(ref: HTMLInputElement): void => {
                          if (ref?.checked) {
                            setSelectedCountyInput(ref);
                          }
                        }}
                        defaultChecked={
                          county.fips === props.initialFormValues?.fips
                        }
                        value={JSON.stringify(county)}
                        onChange={(e: ChangeEvent<HTMLInputElement>) => {
                          setCurrentFormValues({
                            ...currentFormValues,
                            county: JSON.parse(e.target.value),
                          });
                        }}
                        label={`${county.name}, ${county.state}`}
                      />
                    ))}
                  </div>
                  {countyErrorMessage && (
                    <div className=" ds-u-padding-top--1">
                      <InlineError>{countyErrorMessage}</InlineError>
                    </div>
                  )}
                </fieldset>
              </>
            )}
          </div>
        </div>

        <div className="ds-u-padding-top--2">
          {!hasSavedPharmaciesAndDrugs && (
            <span
              className="ds-text-body--md ds-u-padding-right--1"
              data-testid="next-step-text"
            >
              {t("coverage_selector.add_drugs_pharmacies_text")}
            </span>
          )}

          <span className="ds-text-body--md" data-testid="need-help-text">
            <Link
              to={needHelpLinkUrl}
              onClick={() => {
                dispatch({
                  type: AnalyticsActionType.SEND_INTERNAL_LINK_CLICKED_EVENT,
                  settings: {
                    linkText: needHelpText,
                    linkUrl: needHelpLinkUrl,
                  },
                });
              }}
            >
              {needHelpText}
            </Link>
          </span>
        </div>

        <div className="ds-u-padding-top--2">
          <Button
            variation="solid"
            className="mct-c-coverage-selector__start-button"
            data-testid="start-button"
            type="submit"
            onClick={() =>
              dispatch({
                type: AnalyticsActionType.SEND_BUTTON_ENGAGEMENT_EVENT,
                settings: {
                  button: {
                    buttonStyle: AnalyticsButtonStyle.DEFAULT,
                    buttonType: AnalyticsButtonType.SUBMIT,
                    text: startButtonText,
                  },
                },
              })
            }
          >
            {startButtonText}
          </Button>
        </div>
      </form>
    </div>
  );
};
